<script lang="ts" setup>
import {
  callOnce,
  computed,
  onMounted,
  ref,
  useAuth,
  useUser,
  useDialog,
  useMetrics,
  useRouter,
  useTemplateRef,
  useSpots,
  useSpotMedia,
  useToast,
  watch,
  useMaps,
  useCreators,
  usePlaces,
  navigateTo,
  type UserTier,
} from "#imports";
import MPShare from "~/components/dialogs/MPShare.vue";
import MPImg from "~/components/widgets/MPImg.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPBookmark from "~/components/widgets/MPBookmark.vue";
import MPViewportSpill from "~/components/layouts/MPViewportSpill.vue";
import MPSpotCard from "~/components/layouts/MPSpotCard.vue";
import MPFollow from "~/components/widgets/MPFollow.vue";
import MPSpotTierOverlay from "~/components/layouts/MPSpotTierOverlay.vue";
import MPMedia from "~/components/layouts/MPMedia.vue";
import {
  onKeyUp,
  useBreakpoints,
  useSwipe,
  useElementSize,
  useEventListener,
} from "@vueuse/core";
import type { SpotMedia } from "~/stores/spotMedia";

const props = defineProps<{
  spotId: string;
  scrollingContainer: HTMLElement;
}>();

const emit = defineEmits<{
  close: [];
  next: [];
  prev: [];
  subscribe: [value: UserTier];
  setFullScreen: [value: boolean];
  preview: [value: boolean];
}>();

const { openDialog, currentComp, isOpen } = useDialog();
const { trackEvent } = useMetrics();
const router = useRouter();
const { showError } = useToast();

const authStore = useAuth();
const userStore = useUser();
const spotStore = useSpots();
const mapStore = useMaps();
const creatorStore = useCreators();
const spotMediaStore = useSpotMedia();
const placeStore = usePlaces();

const breakpoints = useBreakpoints({ mobile: 920 }, { ssrWidth: 920 });
const isMobile = breakpoints.smaller("mobile");

const spot = computed(() => {
  const spot = spotStore.getById(props.spotId).value!;
  const map = mapStore.getById(spot?.map_id).value!;
  const creator = creatorStore.getById(map?.user_id).value!;
  const media = spotMediaStore.mediaById!;
  const place = placeStore.getById(spot.place_id).value;

  return {
    ...spot,
    map,
    creator,
    media,
    place,
    similarSpots: spot.category_id
      ? (spotStore.similarSpots?.map((sSpot) => ({
          ...sSpot,
          map: mapStore.getById(sSpot.map_id).value!,
        })) ?? [])
      : [],
  };
});

const loadData = async () => {
  const spot = await spotStore.fetchById(props.spotId);
  const map = await mapStore.fetchById(spot.value!.map_id);
  const creator = await creatorStore.fetchById(map.value!.user_id);
  await spotMediaStore.fetchSpotMediaBydId({ spot_id: props.spotId });
  if (spot.value!.place_id) {
    await placeStore.fetchById(spot.value!.place_id);
  }

  if (spot.value!.category_id) {
    const similarSpots = await spotStore.fetchSimilarSpots({
      limit: 10,
      category_id: spot.value!.category_id,
      user_id: creator.value!.id,
    });
    const mapIds = similarSpots.value.map((spot) => spot.map_id);
    await mapStore.fetchByIds(mapIds);
  }
};

// we have to make the key unique to the component because otherwise it won't run more than once
await callOnce(`SpotView-${props.spotId}`, loadData, { mode: "navigation" });

const isFullscreen = ref<boolean>(false);
const inlinePadding = computed(() => {
  if (isMobile.value) {
    return "12px";
  }
  return isFullscreen.value ? "4rem" : "1.5rem";
});

watch(isMobile, () => {
  if (isMobile.value) {
    isFullscreen.value = false;
  }
});

const bgImage = computed(() => `url(${spot.value?.cover_image_url})`);

const userIsSupporter = userStore.isSupporter(spot.value?.creator?.id);

const userIsMapAuthor = computed(() => {
  if (!userStore.user.id) return false;
  return userStore.user.id === spot.value?.creator?.id;
});

const mediaDialogOpen = ref(false);

const openMediaDialog = (media: SpotMedia[], index: number) => {
  openDialog(MPMedia, {
    mediaIds: media.map((m) => m.id),
    index,
  });
};

watch([currentComp, isOpen], ([newComp, newIsOpen], [oldComp]) => {
  if (newComp === MPMedia && newIsOpen) {
    mediaDialogOpen.value = true;
  } else if (oldComp === MPMedia && !newIsOpen) {
    mediaDialogOpen.value = false;
  }
});

onKeyUp("ArrowLeft", () => {
  if (mediaDialogOpen.value) {
    return;
  } else emit("prev");
});
onKeyUp("ArrowRight", () => {
  if (mediaDialogOpen.value) {
    return;
  } else emit("next");
});

const handleClose = () => {
  isFullscreen.value = false;
  emit("setFullScreen", isFullscreen.value);
  emit("close");
};
const spotHeaderTopSwipe = ref(null);
const spotHeaderBottomSwipe = ref(null);
const { isSwiping: topSwipe, direction: topDirection } =
  useSwipe(spotHeaderTopSwipe);
const { isSwiping: botSwipe, direction: botDirection } = useSwipe(
  spotHeaderBottomSwipe,
);

watch([topSwipe, botSwipe], () => {
  if (topSwipe.value && topDirection.value === "right") {
    emit("prev");
  } else if (topSwipe.value && topDirection.value === "left") {
    emit("next");
  } else if (botSwipe.value && botDirection.value === "right") {
    emit("prev");
  } else if (botSwipe.value && botDirection.value === "left") {
    emit("next");
  }
});

const handleCoverTouch = (e: TouchEvent) => {
  if (!e) return;
  const target = e.target as HTMLElement;
  if (target.closest("button") || target.closest(".summary-toggle-button")) {
    return;
  }
  if (!e.changedTouches[0]) return;
  if (e.changedTouches[0].clientY > (window.innerHeight / 6) * 5) {
    return;
  }
  if (e.changedTouches[0].clientX > (window.innerWidth / 6) * 5) {
    emit("next");
  } else if (e.changedTouches[0].clientX < window.innerWidth / 6) {
    emit("prev");
  }
};

onMounted(() => {
  trackEvent("viewed_spot", {
    creator_id: spot.value.creator.id,
    creator_handle: spot.value.creator.mappr_handle || "",
    spot_id: spot.value.id,
    spot_name: spot.value.name || "",
    spot_destination: spot.value.place?.fullName || "",
    spot_category: spot.value.categories?.name || "",
    map_name: spot.value.map.title || "",
    map_id: spot.value.maps.id,
  });
});

const showPreviewOverlay = computed(() => {
  if (!spot.value?.id || userIsMapAuthor.value || userStore.isAdmin) {
    return false;
  }

  const visibilityChecks = {
    public: false,
    authenticated: !authStore.authUser,
    subscriber: !userStore.isPremium,
    supporter: !userIsSupporter.value,
  };

  if (visibilityChecks?.[spot.value?.visibility ?? "public"]) {
    emit("preview", true);
  } else {
    emit("preview", false);
  }

  return visibilityChecks?.[spot.value?.visibility ?? "public"] ?? false;
});

const openShareDialog = () => {
  try {
    openDialog(MPShare, {
      id: spot.value.id,
      version: "spot",
    });
  } catch (e) {
    showError("Error opening share dialog", e);
  }
};

/* Scroll To Logic */
const mediaGalleryRef = useTemplateRef("mediaGalleryRef");
const directionRef = useTemplateRef("directionRef");
const selectedByRef = useTemplateRef("selectedByRef");
const similarSpotsRef = useTemplateRef("similarSpotsRef");
const spotMainRef = useTemplateRef("spotMainRef");
const isButtonsOverContent = ref(false);

const activeSection = ref<string | null>(null);

function scrollTo(
  view: "direction" | "media-gallery" | "similar-spots" | "selected-by",
): void {
  let targetRef;

  if (view === "direction") {
    targetRef = directionRef.value;
  } else if (view === "similar-spots") {
    targetRef = similarSpotsRef.value;
  } else if (view === "media-gallery") {
    targetRef = mediaGalleryRef.value;
  } else if (view === "selected-by") {
    targetRef = selectedByRef.value;
  }

  if (targetRef && props.scrollingContainer) {
    const headerOffset = 60;

    const targetRect = targetRef.getBoundingClientRect();
    const containerRect = props.scrollingContainer.getBoundingClientRect();

    const relativeTop = targetRect.top - containerRect.top;
    const scrollToPosition =
      props.scrollingContainer.scrollTop + relativeTop - headerOffset;

    props.scrollingContainer.scrollTo({
      top: scrollToPosition,
      behavior: "smooth",
    });
  }
}

const handleScrollPosition = () => {
  if (!props.scrollingContainer) return;

  const { scrollTop, scrollHeight, clientHeight } = props.scrollingContainer;

  if (spotMainRef.value) {
    const containerRect = props.scrollingContainer.getBoundingClientRect();
    const mainRect = spotMainRef.value.getBoundingClientRect();

    isButtonsOverContent.value = mainRect.top <= containerRect.top + 50; // 50px threshold
  }

  if (scrollTop + clientHeight >= scrollHeight - 50) {
    activeSection.value = "similar-spots";
    return;
  }

  const sections = [
    { name: "media-gallery", ref: mediaGalleryRef.value },
    { name: "direction", ref: directionRef.value },
    { name: "selected-by", ref: selectedByRef.value },
    { name: "similar-spots", ref: similarSpotsRef.value },
  ].filter((section) => section.ref);

  const containerRect = props.scrollingContainer.getBoundingClientRect();
  const headerOffset = 80;

  let closestSection = null;
  let closestDistance = Infinity;

  sections.forEach((section) => {
    if (!section.ref) return;

    const rect = section.ref.getBoundingClientRect();
    const relativeTop = rect.top - containerRect.top;

    if (relativeTop < containerRect.height && rect.bottom > containerRect.top) {
      const distance = Math.abs(relativeTop - headerOffset);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestSection = section.name;
      }
    }
  });

  activeSection.value = closestSection;
};

useEventListener(props.scrollingContainer, "scroll", handleScrollPosition);

const spotSummaryRef = useTemplateRef<HTMLElement>("spot-summary");
const isSummaryExpanded = ref<boolean>(false);
const shouldShowSummaryToggle = ref<boolean>(false);
const showMoreLineHeight = computed(() => (isMobile.value ? 3 : 4));
const creatorSummaryRef = useTemplateRef<HTMLElement>("creator-summary");
const { height } = useElementSize(creatorSummaryRef);

onMounted(() => {
  if (spotSummaryRef.value) {
    const element = spotSummaryRef.value;
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);

    const maxHeight = lineHeight * showMoreLineHeight.value;
    shouldShowSummaryToggle.value = element.scrollHeight - 8 > maxHeight + 1;
  }

  setTimeout(handleScrollPosition, 100);
});

const toggleSummary = () => {
  isSummaryExpanded.value = !isSummaryExpanded.value;
};

const fullScreenGrid = computed(() => {
  if (!isMobile.value) {
    return "180px 1fr";
  } else {
    return "1fr";
  }
});

const fullScreenDisplay = computed(() => {
  if (isMobile.value) {
    return "none";
  } else {
    return "block";
  }
});

const fullScreenPlaceSelf = computed(() => {
  if (isFullscreen.value) {
    return "start";
  } else {
    return "center";
  }
});

const fullScreenTextAlign = computed(() => {
  if (isMobile.value) {
    return "center";
  } else {
    return "left";
  }
});

const handleGoToProfile = () => {
  router.push(`/@${spot.value.creator.mappr_handle}`);
};
const openGoogleMap = () => {
  if (spot.value.google_place_id) {
    navigateTo(
      `https://www.google.com/maps/place/?q=place_id:${spot.value.google_place_id}`,
      {
        external: true,
        open: { target: "_blank" },
      },
    );
  } else if (spot.value.lat && spot.value.lon) {
    navigateTo(
      `https://www.google.com/maps/search/?api=1&query=${spot.value.lat},${spot.value.lon}`,
      {
        external: true,
        open: { target: "_blank" },
      },
    );
  }
};

const openBookNow = () => {
  window.open(spot.value.booking_link!, "_blank");
};

const openWebsite = () => {
  window.open(spot.value.website!, "_blank");
};
</script>
<template>
  <div>
    <article v-if="spot?.id" class="spot animate-zoom-pan fadeInOut">
      <header class="spot-header" @touchend="handleCoverTouch">
        <menu ref="spotHeaderTopSwipe">
          <MPButton
            v-if="!isMobile"
            hideLabel
            iconOnly
            label="Share Spot"
            size="medium"
            rounded
            backgroundColor="hsla(0, 0%, 60%, 0.25)"
            iconColor="white"
            icon="ri-share-line"
            version="secondary"
            class="header-menu-button share-spot-button"
            @click="openShareDialog"
          />
          <MPButton
            v-else-if="isMobile"
            hideLabel
            iconOnly
            label="Close Spot"
            size="medium"
            rounded
            backgroundColor="hsla(0, 0%, 60%, 0.25)"
            iconColor="white"
            icon="ri-close-line"
            version="secondary"
            class="close-spot-button header-menu-button"
            @click="handleClose"
          />
          <div class="header-buttons-right">
            <MPButton
              v-if="isMobile"
              hideLabel
              iconOnly
              label="Share Spot"
              size="medium"
              rounded
              backgroundColor="hsla(0, 0%, 60%, 0.25)"
              iconColor="white"
              icon="ri-share-line"
              version="secondary"
              class="header-menu-button share-spot-button"
              @click="openShareDialog"
            />
            <MPButton
              v-if="!isMobile"
              hideLabel
              iconOnly
              label="Close Spot"
              size="medium"
              rounded
              backgroundColor="hsla(0, 0%, 60%, 0.25)"
              iconColor="white"
              icon="ri-close-line"
              version="secondary"
              class="close-spot-button header-menu-button"
              @click="handleClose"
            />
            <MPBookmark
              v-if="spot.id && isMobile"
              :id="spot.id"
              link
              rounded
              :hideLabel="true"
              backgroundColor="hsla(0, 0%, 60%, 0.25)"
              iconColor="var(--color-white)"
              bookmark="spot_bookmarks"
            />
            <MPFollow
              v-if="isMobile"
              link
              rounded
              :iconOnly="true"
              backgroundColor="hsla(0, 0%, 60%, 0.25)"
              :creatorId="spot.maps.profiles.id"
              version="secondary"
            />
            <MPButton
              v-if="
                isMobile && (spot.google_place_id || (spot.lat && spot.lon))
              "
              label="Open in Google Maps"
              size="medium"
              iconOnly
              hideLabel
              rounded
              backgroundColor="hsla(0, 0%, 60%, 0.25)"
              iconColor="white"
              icon="ri-external-link-line"
              version="secondary"
              @click="openGoogleMap()"
            />
          </div>
        </menu>
        <article class="spot-header-info-wrappers">
          <div class="spot-header-info" ref="spotHeaderBottomSwipe">
            <p
              class="spot-location"
              v-if="spot.place"
              :class="{
                'spot-location-mobile': isMobile,
                'spot-location-fullscreen': isFullscreen,
              }"
            >
              {{ spot.place.regionCountry }} - {{ spot.place.place }}
            </p>
            <h1
              class="spot-name"
              :class="{
                'spot-name-mobile': isMobile,
                'spot-name-fullscreen': isFullscreen,
              }"
            >
              {{ spot.name }}
            </h1>

            <p
              class="by-creator"
              :class="{
                'by-creator-mobile': isMobile,
                'by-creator-fullscreen': isFullscreen,
              }"
            >
              By
              <span> @{{ spot.creator.mappr_handle }}</span>
            </p>
            <div v-if="!isMobile" class="spot-action-buttons">
              <MPBookmark
                :id="spot.id"
                :size="isMobile ? 'small' : 'medium'"
                :hideLabel="true"
                backgroundColor="var(--color-rose-500)"
                iconColor="var(--color-white)"
                bookmark="spot_bookmarks"
              />
              <MPFollow
                :size="isMobile ? 'small' : 'medium'"
                :creatorId="spot.creator.id"
                version="secondary"
              />
              <MPButton
                v-if="spot.google_place_id || (spot.lat && spot.lon)"
                label="Open in Google Maps"
                :size="isMobile ? 'small' : 'medium'"
                icon="ri-external-link-line"
                version="secondary"
                @click="openGoogleMap()"
              />
            </div>
            <div v-if="spot.summary" class="spot-summary-wrapper">
              <p
                ref="spot-summary"
                class="spot-summary"
                :class="{
                  expanded: isSummaryExpanded,
                  'spot-summary-mobile': isMobile,
                  'spot-summary-fullscreen': isFullscreen,
                }"
              >
                {{ spot.summary }}
              </p>
              <small
                v-if="shouldShowSummaryToggle"
                class="summary-toggle-button"
                :class="{
                  'summary-toggle-button-mobile': isMobile,
                }"
                @click="toggleSummary"
              >
                {{ isSummaryExpanded ? "Show Less" : "Show More" }}
              </small>
            </div>

            <p
              v-if="spot.formatted_address && !isMobile"
              class="spot-address"
              :class="{
                'spot-address-fullscreen': isFullscreen,
              }"
            >
              <span>Address: </span>{{ spot.formatted_address }}
            </p>

            <div
              class="spot-action-links"
              v-if="spot.website || spot.booking_link"
            >
              <MPButton
                v-if="spot.booking_link"
                label="Book now"
                :size="'small'"
                icon="ri-external-link-line"
                version="secondary"
                @click="openBookNow()"
              />
              <MPButton
                v-if="spot.website"
                label="Visit website"
                :size="'small'"
                icon="ri-external-link-line"
                version="secondary"
                @click="openWebsite()"
              />
            </div>
          </div>
        </article>
      </header>
      <div
        class="header-link-buttons-wrapper"
        :class="{ 'over-content': isButtonsOverContent }"
        ref="headerLinkButtonsWrapper"
      >
        <MPViewportSpill ref="headerLinkButtons" class="header-link-buttons">
          <p
            :class="{ active: activeSection === 'media-gallery' }"
            v-if="(spot.media?.length ?? 0) > 0"
            @click="scrollTo('media-gallery')"
          >
            Gallery
          </p>
          <p
            :class="{ active: activeSection === 'direction' }"
            v-if="spot.transport"
            @click="scrollTo('direction')"
          >
            Directions
          </p>
          <p
            :class="{ active: activeSection === 'selected-by' }"
            @click="scrollTo('selected-by')"
          >
            Selected By
          </p>
          <p
            :class="{ active: activeSection === 'similar-spots' }"
            @click="scrollTo('similar-spots')"
          >
            Similar Spots
          </p>
        </MPViewportSpill>
      </div>
      <main class="spot-info" ref="spotMainRef">
        <section class="spot-details">
          <article
            v-if="(spot.media?.length ?? 0) > 0"
            ref="mediaGalleryRef"
            class="media-gallery"
          >
            <MPViewportSpill :showControls="!isMobile">
              <div
                class="media-container"
                v-for="(media, index) in spot.media"
                :key="media.id"
              >
                <MPImg
                  v-if="media.type === 'image'"
                  :src="media.url"
                  :height="283"
                  :width="283"
                  @click="openMediaDialog(spot.media, index)"
                />
                <video
                  controls
                  disablePictureInPicture
                  v-else-if="media.type === 'video'"
                  :src="media.url"
                  @click.prevent="openMediaDialog(spot.media, index)"
                />
                <div v-if="media.attribute" class="attribute typeface-small">
                  <span v-html="media.attribute"></span> &copy;
                </div>
              </div>
            </MPViewportSpill>
          </article>

          <article>
            <ul
              class="spot-tidbits"
              :class="{
                'spot-tidbits-mobile': isMobile,
                'spot-tidbits-fullscreen': isFullscreen,
              }"
            >
              <li v-if="spot.why_go">
                <h4 class="tidbit-title">Why go</h4>
                <p class="tidbit-summary">{{ spot.why_go }}</p>
              </li>
              <li v-if="spot.known_for">
                <h4 class="tidbit-title">Known for</h4>
                <p class="tidbit-summary">{{ spot.known_for }}</p>
              </li>
              <li v-if="spot.transport" ref="directionRef">
                <h4 class="tidbit-title">Transport</h4>
                <p class="tidbit-summary">{{ spot.transport }}</p>
              </li>
              <li v-if="spot.vibe">
                <h4 class="tidbit-title">Vibe</h4>
                <p class="tidbit-summary">{{ spot.vibe }}</p>
              </li>
              <li v-if="spot.tips">
                <h4 class="tidbit-title">Tips</h4>
                <p class="tidbit-summary">{{ spot.tips }}</p>
              </li>
            </ul>
          </article>
          <article
            v-if="spot.map.current_slug"
            class="about-creator"
            :class="{
              'about-creator-mobile': isMobile,
              'about-creator-fullscreen': isFullscreen,
            }"
            ref="selectedByRef"
          >
            <p class="creator-title">
              About <span>@{{ spot.creator.mappr_handle }}</span>
            </p>
            <div class="creator-image">
              <NuxtLinkLocale :to="`/@${spot.creator.mappr_handle}`">
                <MPImg
                  :src="
                    spot.creator.profile_image_url ??
                    '/media/images/placeholder-user.webp'
                  "
                  :height="150"
                  :width="150"
                />
              </NuxtLinkLocale>
              <MPFollow :creatorId="spot.creator.id" class="creator-follow" />
            </div>
            <div class="creator-summary">
              <p
                ref="creator-summary"
                :class="{
                  'creator-summary-mobile': isMobile,
                }"
              >
                {{ spot.creator.bio_text }}
              </p>
              <p
                v-if="height > 170"
                @click="handleGoToProfile"
                class="creator-show-more-button"
              >
                Show More
              </p>
            </div>
          </article>
          <article
            v-if="spot.similarSpots.length > 5"
            class="similar-spots"
            :class="{
              'similar-spots-mobile': isMobile,
              'similar-spots-fullscreen': isFullscreen,
            }"
            ref="similarSpotsRef"
          >
            <h5>Similar Spots</h5>
            <MPViewportSpill
              :showControls="!isMobile"
              class="similar-spot-list"
            >
              <MPSpotCard
                v-for="similarSpot in spotStore.similarSpots"
                :key="similarSpot.id"
                :spotId="similarSpot.id"
                showDestination
                showTier
              />
            </MPViewportSpill>
          </article>
        </section>
      </main>
    </article>
    <div v-if="showPreviewOverlay" class="preview-overlay">
      <MPSpotTierOverlay
        :visibility="spot.visibility"
        @subscribe="emit('subscribe', $event)"
        @close="handleClose"
      />
    </div>
  </div>
</template>

<style scoped lang="postcss">
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/layouts.pcss";
@import "@/assets/css/scrollbar.pcss";

$gridGap: 1rem;
@keyframes fadeInOut {
  0% {
    opacity: 0;
    background-size: auto 120%;
  }
  2% {
    opacity: 1;
  }
  100% {
    background-size: auto 100%;
  }
}

.fadeInOut {
  animation: fadeInOut 20s ease-in-out;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  z-index: 200;

  padding: 1.5rem 1rem 1rem;
}

.spot {
  z-index: 50;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  @mixin scrollbar-reset;
  width: 100vw;
  min-height: calc(100dvh - 6.5rem);
  max-height: 100%;
  scrollbar-width: none;
  padding-bottom: 3rem;
  background-color: white;

  @mixin screen xs {
    z-index: 50;
    max-width: 100%;
    position: relative;
    max-height: 100%;
    height: fit-content;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.header-link-buttons-wrapper {
  margin-top: -3rem;
  position: sticky;
  top: 0;
  z-index: 100;

  &.over-content {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);

    .header-link-buttons {
      p {
        color: var(--color-slate-500);

        &:hover {
          color: var(--color-slate-900);
        }

        &.active {
          color: var(--color-slate-700);
          font-weight: 700 !important;
        }
      }
    }
  }

  .header-link-buttons {
    display: flex;
    gap: 2rem;
    padding-block: 0.75rem;
    color: var(--color-white);

    &:deep(.swipe-content) {
      padding-inline-start: v-bind(inlinePadding) !important;
    }

    p {
      margin: 0;
      padding: 0;
      cursor: pointer;
      font-weight: 500;
      transition: color 0.2s ease;
      color: var(--color-slate-400);

      &:hover {
        color: var(--color-slate-300);
      }
      &.active {
        color: var(--white);
        font-weight: 500 !important;
        border-bottom: 2px solid var(--color-primary);
      }
    }
  }
}

.spot header {
  color: var(--color-white);
  position: relative;
  z-index: 2;
  display: grid;
  min-height: 93dvh;
  max-height: 93dvh;

  background:
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.8),
      rgba(0, 0, 0, 0) 20%,
      rgba(0, 0, 0, 0.85) 68%
    ),
    v-bind(bgImage);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  border-radius: 0rem 0rem 1rem 1rem;

  @mixin screen xs {
    min-height: 45rem;
    max-height: 55rem;
    border-radius: 0rem 0rem 1rem 0rem;
  }

  menu {
    display: flex;
    margin: 0;
    padding: 0;
    margin-block-start: v-bind('isMobile ? "30px" : "21px"');
    padding-inline: v-bind(inlinePadding);

    list-style: none;
    justify-content: space-between;
    height: fit-content;

    button :deep(i) {
      color: white !important;
    }

    .header-menu-button {
      max-height: 2rem !important;
      border: none !important;
    }

    .share-spot-button :deep(i) {
      font-size: 1.3rem;
    }

    .expand-spot-button :deep(i),
    .collapse-spot-button :deep(i) {
      font-size: 1.4rem;
    }

    .close-spot-button :deep(i) {
      font-size: 1.5rem;
    }

    .header-buttons-right {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      @mixin screen sm {
        flex-direction: row;
      }

      .close-spot-button {
        color: white;
        z-index: 1000;
        cursor: pointer;
      }
    }
  }

  .spot-header-info-wrappers {
    display: grid;
    grid-template-rows: 1fr 2rem;
    text-align: left;
    margin-bottom: 1.75rem;

    .spot-header-info {
      place-self: end start;
      width: 100%;

      .spot-name {
        padding-inline: v-bind(inlinePadding);
        font-size: 32px;
        font-weight: 500;
        line-height: 108%;
        margin: 0;
      }

      .spot-name-mobile {
        margin-top: 2rem;
        font-size: 32px;
        font-weight: 500;
      }

      .spot-name-fullscreen {
        font-size: 40px;
      }

      small {
        color: var(--color-rose-400);
      }

      .spot-summary-wrapper {
        padding-inline: v-bind(inlinePadding);
        max-width: 77ch;
        margin-bottom: 2rem;
      }

      .spot-summary {
        margin: 0;
        max-height: 4lh;
        overflow: hidden;
        transition: max-height 0.3s ease;
        line-height: 120% !important;

        &.expanded {
          max-height: 8lh;
          overflow-y: auto;

          &::-webkit-scrollbar {
            display: none;
          }
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      }

      .spot-summary-mobile {
        max-height: 3lh;
      }

      .spot-summary-fullscreen {
        font-size: 20px;
      }

      .spot-summary-wrapper:has(.summary-toggle-button) .spot-summary {
        margin-bottom: 0.5rem;
      }

      .summary-toggle-button {
        color: v-bind(
          'isMobile ? "var(--color-white)" : "var(--color-slate-400)"'
        );
        font-size: v-bind('isMobile ? "13px" : "10px"');
        margin: 0;
        cursor: pointer;
      }

      .summary-toggle-button-mobile {
        color: var(--color-white);
      }

      .spot-address {
        padding-inline: v-bind(inlinePadding);
        font-size: 14px;
        line-height: 120% !important;
        margin: 0;

        span {
          font-weight: 700;
        }
      }

      .spot-address-fullscreen {
        font-size: 16px;
      }

      .spot-location {
        font-size: 14px;
        padding-inline: v-bind(inlinePadding);
        color: var(--color-rose-400);
        margin: 0;
      }

      .spot-location-mobile {
        font-size: 13px;
      }

      .spot-location-fullscreen {
        font-size: 16px;
      }

      .by-creator {
        font-size: 14px;
        padding-inline: v-bind(inlinePadding);
        margin-top: 0.25rem;
        span {
          color: var(--color-rose-400);
        }
      }

      .by-creator-mobile {
        font-size: 13px;
      }

      .by-creator-fullscreen {
        font-size: 16px;
      }

      .spot-action-buttons {
        padding-inline: v-bind(inlinePadding);
        position: relative;
        display: flex;
        gap: 0.5rem;
        margin-bottom: 2.25rem;
      }

      .spot-action-links {
        padding-inline: v-bind(inlinePadding);
        position: relative;
        display: flex;
        gap: 0.5rem;
        margin-top: 1.5rem;
      }
    }
  }
}

.spot main {
  position: relative;
  z-index: 1;
  margin-top: -1rem;
  background-color: var(--main-background-color);
  text-align: left;
  height: fit-content;

  .spot-address-mobile {
    font-size: 14px;
    color: var(--color-slate-400);
    span {
      font-weight: 500;
    }
  }

  .spot-details {
    padding-top: 1rem;

    .media-gallery {
      margin-top: 1rem;
      height: 283px;
      margin-bottom: 5rem;

      @mixin screen sm {
        margin-bottom: 3rem;
      }

      :first-child {
        gap: 1rem;
      }

      :deep(.swipe-content) {
        padding-inline-start: v-bind(inlinePadding);
        border-radius: 1rem;
      }

      img,
      video {
        height: 283px;
        max-width: inherit;
        width: 283px;
        border-radius: 1rem;
      }
    }

    .spot-tidbits {
      list-style: none;
      margin: 0;
      padding: 0;
      margin-top: 3rem;

      padding-inline: v-bind(inlinePadding);
      li {
        gap: 1rem;

        .tidbit-title {
          margin-top: 2rem !important;
          font-size: 20px;
          font-weight: 900 !important;
          color: var(--color-slate-500);
          margin: 0;
        }
        .tidbit-summary {
          font-weight: 400 !important;
          color: var(--color-slate-700) !important;
          margin-block: 0.5rem;
          font-size: 16px;
          line-height: 120% !important;
        }
      }
    }

    .spot-tidbits-mobile {
      .tidbit-title {
      }
    }

    .spot-tidbits-fullscreen {
      li {
        max-width: 90ch !important;
      }
      .tidbit-title {
        font-size: 25px !important;
      }

      .tidbit-summary {
        font-size: 20px !important;
        max-width: 90ch !important;
      }
    }
  }

  .about-creator {
    margin-top: 3rem;
    position: relative;
    display: grid;
    grid-template-columns: v-bind(fullScreenGrid);
    padding-inline: v-bind(inlinePadding);
    .creator-title {
      font-size: 14px;
      font-weight: 700 !important;
      color: var(--color-slate-700);
      display: v-bind(fullScreenDisplay);
      place-self: start;
      grid-column: 1/-1;

      span {
        color: var(--color-rose-400);
      }
    }
    .creator-image {
      display: grid;

      place-self: v-bind(fullScreenPlaceSelf);
      align-self: center;
      img {
        border-radius: 100vw;
        width: 150px;
        height: 150px;
        margin: auto;
      }
      .creator-follow {
        margin-top: -1.5rem;
      }
    }
    .creator-summary {
      max-height: 170px;
      line-height: 1.25 !important;
      position: relative;
      text-align: v-bind(fullScreenTextAlign);
      padding-inline: 1.5rem;
      font-size: 16px;
      color: var(--color-slate-700);
      max-width: 70ch;
      overflow: hidden;

      .creator-summary-mobile {
        margin-top: 1rem;
      }

      p {
        margin: 0;
      }

      .creator-show-more-button {
        position: absolute;
        bottom: 0;
        background-color: white;
        left: 0;
        right: 0;
        cursor: pointer;
        text-align: center;
      }

      .creator-show-more-button::before {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        height: 2lh;
        background: linear-gradient(to bottom, transparent, white);
        pointer-events: none;
      }
    }
  }

  .about-creator-mobile {
    margin-top: 3.825rem;
    .creator-summary {
      p {
        font-size: 16px !important;
        line-height: 120% !important;
      }
    }
  }

  .about-creator-fullscreen {
    .creator-title {
      font-size: 18px !important;
    }

    .creator-summary {
      place-content: center;
      p {
        font-size: 20px !important;
        line-height: 120% !important;
      }
    }
  }

  .similar-spots {
    padding-top: 3rem;

    :deep(.swipe-content) {
      padding-inline-start: v-bind(inlinePadding);
      border-radius: 1rem;
    }
    h5 {
      padding-inline-start: v-bind(inlinePadding);
      font-size: 14px;
      font-weight: 500 !important;
      color: var(--color-slate-500);
      margin-bottom: 1rem;
    }
  }

  .similar-spots-mobile {
    padding-top: 2rem;
    h5 {
      font-size: 14px;
    }
  }

  .similar-spots-fullscreen {
    h5 {
      font-size: 18px;
    }
  }

  .similar-spot-list {
    width: 100%;
    gap: $gridGap;
    &:deep(.spot) {
      width: calc(($layoutWide / 5) - $gridGap);
    }
  }
}

.media-container {
  position: relative;
}

.attribute {
  position: absolute;
  bottom: 0.25rem;
  right: 0.25rem;
  background: white;
  border-radius: 1rem;
  padding: 0.1rem 0.25rem;
}
</style>
